// ─── src/pages/PublicationsReaderPage.jsx ────────────────────────────────────
// Full-screen flipbook reader, Tier 1 Premium Reader
// Route: /publications/[slug]
//
// Features: zoom+pan, TOC panel, bookmarks, share, URL sync,
//           reader mode (dark/sepia), cinematic intro, reading progress bar,
//           enhanced page counter, keyboard shortcuts, pinch-to-zoom

import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef } from 'react';
import { createPortal } from 'react-dom';
// react-pageflip kept installed for one stable cycle as rollback insurance,
// but no longer used. The new LuxuryFlipBook component (built directly on
// the underlying page-flip / StPageFlip engine) replaces it and resolves
// the mobile black-screen bug that the abandoned wrapper couldn't handle.
import LuxuryFlipBook from '../components/reader/LuxuryFlipBook';
import LuxeEnquiryModal from '../components/enquiry/LuxeEnquiryModal';
import { Helmet } from 'react-helmet-async';
import { fetchIssueBySlug, fetchPublishedIssueBySlug } from '../services/magazineIssuesService';
import { ReaderTopBar, ReaderTOCPanel } from '../components/reader/ReaderChrome';
import { fetchPages }       from '../services/magazinePageService';
import { usePrerenderReady } from '../utils/prerenderReady';
import { buildCacheKey, bustUrl } from './PublicationStudio/_cacheBust';
import { trackIssueView, trackPageTurn, trackDownload } from '../services/publicationsAnalyticsService';
import { supabase }         from '../lib/supabaseClient';
import SocialExportModal    from '../components/publications/SocialExportModal';
import IssueSearchPanel     from '../components/publications/IssueSearchPanel';
import TextModePanel        from '../components/publications/TextModePanel';
import EmailGate            from './PublicationsReader/EmailGate';
import { useAdminAuth }     from '../context/AdminAuthContext';
import { imgUrl } from '../utils/imgUrl.js';

// ── Email gate config (Phase 3: lead capture) ────────────────────────────────
const GATE_PAGE_TRIGGER     = 3;      // open gate when user reaches page 3
const GATE_TIME_FALLBACK_MS = 25000;  // or after 25s of reading, whichever first
const GATE_STORAGE_KEY      = 'lwd_pub_gate_unlocked';

// ── Design tokens ──────────────────────────────────────────────────────────────
const GOLD      = '#d39a21';
const GD        = "var(--font-heading-primary, 'Cormorant Garamond', Georgia, serif)";
const NU        = "var(--font-body, 'Jost', sans-serif)";
const MUTED     = 'rgba(255,255,255,0.38)';
const CTRL_BG   = 'rgba(0,0,0,0.55)';
const CTRL_HOV  = 'rgba(211,154,33,0.18)';

// Reader theme system, dark (lights off) vs light (lights on)
const READER_THEMES = {
  dark: {
    bg:       '#080706',
    pageBg:   '#0F0D0A',
    ctrlBg:   'rgba(0,0,0,0.6)',
    ctrlText: 'rgba(255,255,255,0.72)',
    ctrlBdr:  'rgba(255,255,255,0.1)',
    muted:    'rgba(255,255,255,0.38)',
    topGrad:  'linear-gradient(to bottom, rgba(8,7,6,0.96) 0%, rgba(8,7,6,0) 100%)',
    btnHov:   'rgba(211,154,33,0.18)',
    text:     '#ffffff',
    shadow:   '0 8px 48px rgba(0,0,0,0.6)',
    pageShad: '0 8px 48px rgba(0,0,0,0.6)',
  },
  light: {
    bg:       '#EAE5DC',
    pageBg:   '#FFFFFF',
    ctrlBg:   'rgba(255,255,255,0.88)',
    ctrlText: 'rgba(24,18,10,0.72)',
    ctrlBdr:  'rgba(24,18,10,0.12)',
    muted:    'rgba(24,18,10,0.45)',
    topGrad:  'linear-gradient(to bottom, rgba(234,229,220,0.97) 0%, rgba(234,229,220,0) 100%)',
    btnHov:   'rgba(211,154,33,0.15)',
    text:     '#18120A',
    shadow:   '0 4px 28px rgba(0,0,0,0.12)',
    pageShad: '0 8px 40px rgba(0,0,0,0.14)',
  },
};

// ── Bookmark helpers ──────────────────────────────────────────────────────────
function loadBookmarks(issueId) {
  try {
    const raw = localStorage.getItem(`lwd_bm_${issueId}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveBookmarks(issueId, bms) {
  try { localStorage.setItem(`lwd_bm_${issueId}`, JSON.stringify(bms)); } catch {}
}
function toggleBookmark(issueId, pageNum, current) {
  const updated = current.includes(pageNum)
    ? current.filter(n => n !== pageNum)
    : [...current, pageNum];
  saveBookmarks(issueId, updated);
  return updated;
}

// ── Touch distance helper ─────────────────────────────────────────────────────
function getTouchDist(t0, t1) {
  const dx = t0.clientX - t1.clientX;
  const dy = t0.clientY - t1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ── Loading spinner ───────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', background: '#080706', gap: 20,
    }}>
      <div style={{ fontSize: 32, opacity: 0.3, animation: 'spin 2s linear infinite' }}>◈</div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      <span style={{ fontFamily: NU, fontSize: 11, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Loading issue…
      </span>
    </div>
  );
}

// ── Error screen ──────────────────────────────────────────────────────────────
function ErrorScreen({ message, onBack }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', background: '#080706',
      gap: 16, padding: 24, textAlign: 'center',
    }}>
      <span style={{ fontSize: 36, opacity: 0.25 }}>◈</span>
      <p style={{ fontFamily: NU, fontSize: 14, color: MUTED, maxWidth: 320 }}>{message}</p>
      <button
        onClick={onBack}
        style={{
          fontFamily: NU, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: GOLD, background: 'none',
          border: `1px solid ${GOLD}`, padding: '9px 20px', borderRadius: 1,
          cursor: 'pointer', marginTop: 8,
        }}
      >
        Back to Publications
      </button>
    </div>
  );
}

// ── Cinematic intro overlay ───────────────────────────────────────────────────
function IntroOverlay({ issue, onDismiss }) {
  const [fading, setFading] = useState(false);
  // cover_image is the actual schema column; cover_image_url / cover_url are
  // older aliases kept as fallback in case any legacy data still has them.
  const coverUrl = issue.cover_image || issue.cover_image_url || issue.cover_url || null;

  const dismiss = useCallback(() => {
    if (fading) return;
    setFading(true);
    setTimeout(() => onDismiss(), 500);
  }, [fading, onDismiss]);

  useEffect(() => {
    const onKey = () => dismiss();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      background: '#030201',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.5s ease',
    }}>
      {/* Blurred backdrop */}
      {coverUrl && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${imgUrl(coverUrl, { width: 1200, resize: 'contain' })})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'blur(40px) brightness(0.25)',
        }} />
      )}
      {/* Foreground */}
      <div style={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: 24,
      }}>
        {coverUrl && (
          <img
            src={imgUrl(coverUrl, { width: 1200, resize: 'contain' })}
            alt={issue.title}
            style={{
              maxHeight: '65vh',
              maxWidth: 'min(85vw, 380px)',
              objectFit: 'contain',
              boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
              animation: 'introReveal 0.9s ease forwards',
            }}
          />
        )}
        <style>{`
          @keyframes introReveal {
            from { opacity: 0; transform: scale(1.04); }
            to   { opacity: 1; transform: scale(1); }
          }
        `}</style>
        <div style={{
          fontFamily: NU, fontSize: 9, color: GOLD,
          letterSpacing: '0.2em', textTransform: 'uppercase', marginTop: 20,
          textAlign: 'center',
        }}>
          {[issue.issue_number && `Issue ${issue.issue_number}`, issue.season]
            .filter(Boolean).join(' · ')}
        </div>
        <div style={{
          fontFamily: GD, fontStyle: 'italic', fontSize: 28,
          color: '#F0EBE0', textAlign: 'center', maxWidth: 340,
          padding: '0 16px',
        }}>
          {issue.title || 'Untitled Issue'}
        </div>
        <button
          onClick={dismiss}
          style={{
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: GOLD, background: 'transparent',
            border: `1px solid ${GOLD}`,
            padding: '11px 32px', borderRadius: 1, cursor: 'pointer',
            marginTop: 8,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(211,154,33,0.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Open Issue →
        </button>
      </div>
    </div>
  );
}

// ── Reading progress bar ──────────────────────────────────────────────────────
function ProgressBar({ currentPage, totalPages }) {
  const pct = totalPages > 0 ? (currentPage / totalPages) * 100 : 0;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0,
      zIndex: 200, height: 3, background: 'rgba(255,255,255,0.06)',
    }}>
      <div style={{
        height: '100%',
        width: `${pct}%`,
        background: GOLD,
        transition: 'width 0.3s ease',
      }} />
    </div>
  );
}

// ── Toast notification ────────────────────────────────────────────────────────
function Toast({ message, visible }) {
  return (
    <div style={{
      position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
      zIndex: 300,
      background: 'rgba(211,154,33,0.15)',
      border: '1px solid rgba(211,154,33,0.4)',
      color: GOLD,
      padding: '8px 20px', borderRadius: 20,
      fontFamily: NU, fontSize: 11,
      letterSpacing: '0.06em',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.3s ease',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
    }}>
      {message}
    </div>
  );
}

// TOCPanel and TopBar used to be defined here. They now live in
// components/reader/ReaderChrome.jsx so the private Membership Edition reader is
// the SAME bar and the SAME contents panel, not a second one that resembles it.

// ── Hotspot overlay item ──────────────────────────────────────────────────────
function HotspotOverlay({ hotspot, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={hotspot.label}
      style={{
        position:   'absolute',
        left:       hotspot.x + '%',
        top:        hotspot.y + '%',
        width:      hotspot.w + '%',
        height:     hotspot.h + '%',
        border:     `2px solid ${hovered ? '#d39a21' : 'rgba(211,154,33,0.35)'}`,
        background: hovered ? 'rgba(211,154,33,0.12)' : 'rgba(211,154,33,0.04)',
        cursor:     'pointer',
        transition: 'all 0.15s',
        borderRadius: 2,
        display:    'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        padding:    4,
        boxSizing:  'border-box',
      }}
    >
      {hovered && (
        <span style={{
          fontFamily: NU,
          fontSize: 9, fontWeight: 600, color: '#d39a21',
          background: 'rgba(0,0,0,0.8)', padding: '3px 7px',
          borderRadius: 2, letterSpacing: '0.08em', textTransform: 'uppercase',
          maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
          ✦ {hotspot.label}
        </span>
      )}
    </div>
  );
}

// ── Page video overlay ────────────────────────────────────────────────────────
function PageVideoOverlay({ page }) {
  const [playing, setPlaying] = useState(false);
  const url = page?.video_url;
  if (!url) return null;

  const isYT    = url.includes('youtube.com') || url.includes('youtu.be');
  const isVimeo = url.includes('vimeo.com');
  const isIG    = url.includes('instagram.com');
  const isTT    = url.includes('tiktok.com');
  const isPortrait = isIG || isTT; // 9:16 aspect

  // Label for the play button
  const btnLabel = isIG ? '▶ Instagram Reel' : isTT ? '▶ TikTok' : '▶ Watch Video';

  if (!playing) {
    return (
      <button
        onClick={() => setPlaying(true)}
        style={{
          position: 'absolute', bottom: 16, right: 16, zIndex: 60,
          background: 'rgba(0,0,0,0.7)', border: `1px solid ${GOLD}`,
          borderRadius: 2, color: GOLD, padding: '7px 14px',
          fontFamily: NU, fontSize: 9, fontWeight: 600,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {btnLabel}
      </button>
    );
  }

  // Build embed URL
  let embedUrl = url;
  if (isYT) {
    const id = url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([^&?/]+)/)?.[1];
    embedUrl = `https://www.youtube.com/embed/${id}?autoplay=1&muted=1`;
  } else if (isVimeo) {
    const id = url.match(/vimeo\.com\/(\d+)/)?.[1];
    embedUrl = `https://player.vimeo.com/video/${id}?autoplay=1&muted=1`;
  } else if (isIG) {
    const match = url.match(/\/(reel|p)\/([^/?]+)/);
    if (match) embedUrl = `https://www.instagram.com/${match[1]}/${match[2]}/embed/`;
  } else if (isTT) {
    const videoId = url.match(/\/video\/(\d+)/)?.[1];
    if (videoId) embedUrl = `https://www.tiktok.com/embed/v2/${videoId}`;
  }

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 60,
      background: 'rgba(0,0,0,0.92)',
      display: 'flex', flexDirection: 'column',
      alignItems: isPortrait ? 'center' : 'stretch',
      justifyContent: isPortrait ? 'center' : 'flex-start',
    }}>
      <button
        onClick={() => setPlaying(false)}
        style={{
          position: 'absolute', top: 8, right: 8, zIndex: 61,
          background: 'none', border: 'none', color: 'white',
          fontSize: 20, cursor: 'pointer', padding: 8,
        }}
      >
        ✕
      </button>
      {(isYT || isVimeo || isIG || isTT) ? (
        <iframe
          src={embedUrl}
          style={isPortrait
            ? { width: '100%', maxWidth: 380, aspectRatio: '9/16', border: 'none' }
            : { flex: 1, border: 'none' }
          }
          allow="autoplay; fullscreen"
          title="Page video"
        />
      ) : (
        <video
          src={url}
          autoPlay
          muted={page.video_muted !== false}
          controls
          style={{ flex: 1, objectFit: 'contain' }}
        />
      )}
    </div>
  );
}

// ── Prefers-reduced-motion (computed once at module level) ────────────────────
// Avoids creating a new MediaQueryList object on every component render/nav.
// Re-reads correctly on hard refresh; for live system-pref changes during a
// session the user would need to reload the page, acceptable for this use case.
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── Page-flip SFX: intentionally silent ──────────────────────────────────────
// Premium editorial platforms (Vogue Archive, NYT Magazine, Apple Books,
// Kindle, Issuu) all ship silent page turns. LWD follows that convention,
// the paper-curl animation carries the luxury signal on its own.

// ── Page image ────────────────────────────────────────────────────────────────
// Memoised: re-renders only when page data, preview flag, or theme changes.
// Avoids re-rendering every image when the reader theme or current-page changes.
const PageImage = React.memo(function PageImage({ page, side, pageBg, onHotspotClick, isPreview }) {
  const [loaded, setLoaded]     = useState(false);
  const [liveDataUrl, setLive]  = useState(null); // preview-only live render

  // Preview-mode live render: when there's canvasJSON but no published image,
  // render the Fabric canvas to a JPEG dataURL in the browser so the user sees
  // their work-in-progress without needing to run Publish Digital first.
  const hasCanvas = !!page?.template_data?.canvasJSON;
  const hasImage  = !!page?.image_url;
  const shouldLiveRender = isPreview && hasCanvas && !hasImage;

  useEffect(() => {
    if (!shouldLiveRender) return;
    let cancelled = false;
    (async () => {
      try {
        const { Canvas: FC, FabricObject } = await import('fabric');
        FabricObject.ownDefaults.originX = 'left';
        FabricObject.ownDefaults.originY = 'top';
        const offEl = document.createElement('canvas');
        const w = 794, h = 1123;
        offEl.width  = w;
        offEl.height = h;
        const fc = new FC(offEl, { width: w, height: h, enableRetinaScaling: false });
        fc.backgroundColor = '#ffffff';
        await fc.loadFromJSON(page.template_data.canvasJSON);
        fc.renderAll();
        await new Promise(r => setTimeout(r, 60));
        fc.renderAll();
        const url = offEl.toDataURL('image/jpeg', 0.92);
        fc.dispose();
        if (!cancelled) setLive(url);
      } catch (e) {
        console.warn('[reader] live preview render failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [shouldLiveRender, page?.id, page?.template_data]);

  if (!page) {
    return <div style={{ flex: 1, background: pageBg, border: '1px solid rgba(255,255,255,0.04)' }} />;
  }

  const displayUrl = hasImage ? page.image_url : (isPreview ? liveDataUrl : null);

  if (!displayUrl) {
    // No published image and no canvas JSON to fall back on, show placeholder.
    return (
      <div style={{
        flex: 1, position: 'relative', background: pageBg,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 12,
      }}>
        <span style={{ fontSize: 28, opacity: 0.2 }}>◈</span>
        <div style={{
          fontFamily: "'Jost',sans-serif", fontSize: 10,
          color: 'rgba(255,255,255,0.3)', textAlign: 'center',
          letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1.6,
          maxWidth: 200,
        }}>
          Page {page.page_number}<br />
          {isPreview
            ? (hasCanvas
                ? <span style={{ color: '#d39a21', opacity: 0.8 }}>Rendering preview…</span>
                : <span style={{ color: '#d39a21', opacity: 0.8 }}>Not yet designed,<br />open in the studio</span>)
            : <span style={{ color: '#d39a21', opacity: 0.7 }}>Image rendering…</span>
          }
        </div>
      </div>
    );
  }

  const hotspots = page?.link_targets || [];
  return (
    <div style={{ flex: 1, position: 'relative', background: pageBg, overflow: 'hidden' }}>
      {!loaded && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: 20, opacity: 0.15 }}>◈</span>
        </div>
      )}
      <img
        src={imgUrl(displayUrl, { width: 2000, resize: 'contain' })}
        alt={`Page ${page.page_number}`}
        onLoad={() => setLoaded(true)}
        draggable={false}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          display: 'block',
          opacity: loaded ? 1 : 0, transition: 'opacity 0.25s',
          userSelect: 'none', pointerEvents: 'none',
          imageRendering: 'auto',
        }}
      />
      {/* Hotspot overlay, only shown when image is loaded */}
      {loaded && hotspots.map(hs => (
        <HotspotOverlay
          key={hs.id}
          hotspot={hs}
          onClick={() => onHotspotClick?.(hs)}
        />
      ))}
      {/* Video overlay */}
      <PageVideoOverlay page={page} />
    </div>
  );
}); // React.memo, PageImage

// ── BookPage: forwardRef wrapper required by react-pageflip ───────────────────
// HTMLFlipBook needs each child to accept a ref so it can apply 3D transforms
// directly to the DOM node. This thin wrapper bridges PageImage to that API.
const BookPage = forwardRef(function BookPage({ page, pageBg, onHotspotClick, isPreview }, ref) {
  return (
    <div
      ref={ref}
      className="lwd-book-page"
      style={{
        width: '100%', height: '100%',
        background: pageBg,
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <PageImage
        page={page}
        pageBg={pageBg}
        onHotspotClick={onHotspotClick}
        isPreview={isPreview}
      />
    </div>
  );
});

// ── Nav button ────────────────────────────────────────────────────────────────
function NavButton({ direction, onClick, disabled, T }) {
  const [hovered, setHovered] = useState(false);
  const isMob = typeof window !== 'undefined' && window.innerWidth < 640;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'absolute',
        // Mobile: sit at bottom corners so they don't cover the page
        // Desktop: centred on left/right edges as before
        ...(isMob
          ? {
              bottom: 52,
              [direction === 'prev' ? 'left' : 'right']: 8,
              top: 'auto',
              transform: 'none',
            }
          : {
              top: '50%',
              transform: 'translateY(-50%)',
              [direction === 'prev' ? 'left' : 'right']: 16,
            }
        ),
        zIndex: 50,
        width: isMob ? 40 : 48, height: isMob ? 40 : 48, borderRadius: '50%',
        background: hovered ? T.btnHov : T.ctrlBg,
        border: `1px solid ${hovered ? 'rgba(211,154,33,0.45)' : T.ctrlBdr}`,
        color: disabled ? (T.muted) : (hovered ? GOLD : T.ctrlText),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.3 : 1,
        fontSize: 20, transition: 'all 0.15s', userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {direction === 'prev' ? '‹' : '›'}
    </button>
  );
}

// ── Page counter ──────────────────────────────────────────────────────────────
function PageCounter({ currentPage, totalPages, isDouble, bookmarks, T }) {
  const end = isDouble ? Math.min(currentPage + 1, totalPages) : currentPage;
  const pct = totalPages > 0 ? Math.round((currentPage / totalPages) * 100) : 0;
  const isBookmarked = bookmarks.includes(currentPage);

  return (
    <div style={{
      position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
      zIndex: 100,
      background: T.ctrlBg,
      border: `1px solid ${T.ctrlBdr}`,
      borderRadius: 20,
      padding: '6px 18px',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {isBookmarked && (
        <span style={{ color: GOLD, fontSize: 10, marginRight: 2 }}>✦</span>
      )}
      <span style={{ fontFamily: NU, fontSize: 11, color: T.ctrlText, letterSpacing: '0.04em' }}>
        Page {isDouble && end > currentPage ? `${currentPage}/${end}` : currentPage}
      </span>
      <span style={{ color: T.muted, fontSize: 10 }}>/</span>
      <span style={{ fontFamily: NU, fontSize: 11, color: T.muted, letterSpacing: '0.04em' }}>
        {totalPages}
      </span>
      <span style={{ color: T.muted, fontSize: 10 }}>·</span>
      <span style={{ fontFamily: NU, fontSize: 11, color: T.muted, letterSpacing: '0.04em' }}>
        {pct}%
      </span>
    </div>
  );
}

// ── Page reactions (❤ love button) ───────────────────────────────────────────
function PageReactions({ currentPage, counts, myReactions, onReact, T }) {
  const count   = counts[currentPage] || 0;
  const reacted = myReactions.has(currentPage);
  const [pop,   setPop]   = useState(false);

  const handleClick = () => {
    if (reacted) return;
    onReact(currentPage);
    setPop(true);
    setTimeout(() => setPop(false), 600);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 22,
      zIndex: 100,
    }}>
      <button
        onClick={handleClick}
        title={reacted ? 'You loved this page' : 'Love this page'}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: reacted ? 'rgba(211,154,33,0.14)' : T.ctrlBg,
          border: `1px solid ${reacted ? 'rgba(211,154,33,0.4)' : T.ctrlBdr}`,
          borderRadius: 20,
          padding: '6px 12px',
          cursor: reacted ? 'default' : 'pointer',
          transition: 'all 0.2s',
          transform: pop ? 'scale(1.2)' : 'scale(1)',
          outline: 'none',
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1, transition: 'transform 0.15s', transform: pop ? 'scale(1.3)' : 'scale(1)' }}>
          {reacted ? '❤' : '♡'}
        </span>
        {count > 0 && (
          <span style={{ fontFamily: NU, fontSize: 11, color: reacted ? GOLD : T.ctrlText, letterSpacing: '0.02em', minWidth: 10 }}>
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

// ── Paywall gate overlay ──────────────────────────────────────────────────────
function PaywallGate({ issue, freePageCount, onBack, T }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'rgba(8,7,6,0.92)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: '40px 24px',
      textAlign: 'center',
    }}>
      {/* Cover thumbnail */}
      {issue.cover_image && (
        <div style={{
          width: 120, height: 170, borderRadius: 3,
          backgroundImage: `url(${imgUrl(issue.cover_image, { width: 400, resize: 'contain' })})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
          marginBottom: 28, flexShrink: 0,
          opacity: 0.75,
        }} />
      )}

      {/* ✦ icon */}
      <div style={{ color: GOLD, fontSize: 22, letterSpacing: 4, marginBottom: 12 }}>✦</div>

      {/* Headline */}
      <h2 style={{
        fontFamily: GD, fontSize: 28, fontWeight: 400,
        color: '#ffffff', margin: '0 0 10px',
        letterSpacing: '0.02em', lineHeight: 1.2,
      }}>
        Continue Reading
      </h2>

      {/* Sub */}
      <p style={{
        fontFamily: NU, fontSize: 13, color: 'rgba(255,255,255,0.55)',
        margin: '0 0 28px', maxWidth: 380, lineHeight: 1.6,
      }}>
        You've read {freePageCount} complimentary page{freePageCount !== 1 ? 's' : ''} of{' '}
        <em style={{ color: 'rgba(255,255,255,0.75)' }}>{issue.title}</em>.{' '}
        Subscribe to unlock the full issue and every edition in our archive.
      </p>

      {/* CTA buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 300 }}>
        <button
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => window.location.href = '/subscribe'}
          style={{
            background: hovered
              ? 'linear-gradient(135deg, #D4B06A 0%, #C9A96E 100%)'
              : 'linear-gradient(135deg, #C9A96E 0%, #B8935A 100%)',
            border: 'none', borderRadius: 2, color: '#18120A',
            fontFamily: NU, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            padding: '14px 24px', cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: hovered ? '0 4px 20px rgba(201,169,110,0.35)' : 'none',
          }}
        >
          ✦ Subscribe to Read More
        </button>

        <button
          onClick={onBack}
          style={{
            background: 'none', border: `1px solid rgba(255,255,255,0.15)`,
            borderRadius: 2, color: 'rgba(255,255,255,0.45)',
            fontFamily: NU, fontSize: 10, fontWeight: 600,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '10px 24px', cursor: 'pointer', transition: 'color 0.15s',
          }}
        >
          Back to Publications
        </button>
      </div>

      {/* Fine print */}
      <p style={{
        fontFamily: NU, fontSize: 10, color: 'rgba(255,255,255,0.25)',
        margin: '28px 0 0', lineHeight: 1.5, maxWidth: 320,
      }}>
        Subscribers enjoy unlimited access to all issues, plus exclusive editorial content.
      </p>
    </div>
  );
}

// ── Thumbnail strip ───────────────────────────────────────────────────────────
function ThumbnailStrip({ pages, currentPage, onJump, isDesktop, T }) {
  const [open, setOpen] = useState(false);
  if (!isDesktop) return null;
  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 110,
          background: T.ctrlBg, border: `1px solid ${T.ctrlBdr}`,
          borderRadius: 2, color: open ? GOLD : T.ctrlText,
          fontFamily: NU, fontSize: 9, fontWeight: 600,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          padding: '7px 14px', cursor: 'pointer', transition: 'color 0.15s',
        }}
      >
        {open ? '✕ Close' : '⊞ Pages'}
      </button>
      {open && (
        <div style={{
          position: 'fixed', bottom: 60, left: 0, right: 0, zIndex: 105,
          background: T.ctrlBg, borderTop: `1px solid ${T.ctrlBdr}`,
          padding: '12px 20px', display: 'flex', gap: 8, overflowX: 'auto',
        }}>
          {pages.map(p => (
            <button
              key={p.page_number}
              onClick={() => { onJump(p.page_number); setOpen(false); }}
              title={`Page ${p.page_number}`}
              style={{
                flexShrink: 0, width: 52, height: 74, background: T.pageBg,
                border: `1px solid ${p.page_number === currentPage ? GOLD : T.ctrlBdr}`,
                borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                padding: 0, position: 'relative',
              }}
            >
              {p.thumbnail_url
                ? <img src={imgUrl(p.thumbnail_url, { width: 300 })} alt={`p${p.page_number}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <div style={{
                    width: '100%', height: '100%', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontFamily: NU, fontSize: 9, color: T.muted,
                  }}>{p.page_number}</div>
              }
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.7)', textAlign: 'center',
                fontFamily: NU, fontSize: 8, color: 'rgba(255,255,255,0.6)', padding: '2px 0',
              }}>
                {p.page_number}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Zoom reset pill ───────────────────────────────────────────────────────────
function ZoomPill({ zoom, onReset, T }) {
  if (zoom === 1) return null;
  return (
    <button
      onClick={onReset}
      style={{
        position: 'fixed', bottom: 20, left: 20, zIndex: 110,
        background: T.ctrlBg, border: `1px solid ${T.ctrlBdr}`,
        borderRadius: 20, padding: '6px 14px',
        color: T.ctrlText, fontFamily: NU, fontSize: 10,
        fontWeight: 600, letterSpacing: '0.08em', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = T.btnHov}
      onMouseLeave={e => e.currentTarget.style.background = T.ctrlBg}
    >
      {Math.round(zoom * 100)}%
      <span style={{ opacity: 0.5 }}>·</span>
      <span style={{ opacity: 0.7 }}>✕ Reset</span>
    </button>
  );
}

// ── Vendor Credits button ─────────────────────────────────────────────────────
function VendorCreditsButton({ count, open, onToggle, T }) {
  return (
    <button onClick={onToggle} style={{
      position: 'fixed', bottom: 56, left: 20, zIndex: 110,
      background: open ? 'rgba(211,154,33,0.15)' : T.ctrlBg,
      border: `1px solid ${open ? 'rgba(211,154,33,0.5)' : T.ctrlBdr}`,
      borderRadius: 2, color: open ? '#d39a21' : T.ctrlText,
      fontFamily: NU, fontSize: 9, fontWeight: 600,
      letterSpacing: '0.1em', textTransform: 'uppercase', padding: '7px 14px',
      cursor: 'pointer', transition: 'all 0.15s',
    }}>
      ◈ Credits ({count})
    </button>
  );
}

// ── Credits drawer ────────────────────────────────────────────────────────────
function CreditsDrawer({ credits, onClose, T }) {
  const drawerBg = T.ctrlBg !== 'rgba(0,0,0,0.6)' ? 'rgba(240,235,226,0.97)' : 'rgba(10,9,8,0.97)';
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 120,
      background: drawerBg, backdropFilter: 'blur(12px)',
      borderTop: `1px solid ${T.ctrlBdr}`,
      padding: '20px 24px 28px',
      animation: 'slideUp 0.25s ease',
    }}>
      <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontFamily: GD, fontSize: 18, color: T.text, flex: 1 }}>In This Spread</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {credits.map((c, i) => (
          <a
            key={i}
            href={c.profileSlug ? `/vendor/${c.profileSlug}` : (c.website || '#')}
            target={c.website ? '_blank' : undefined}
            rel="noreferrer"
            style={{
              display: 'flex', flexDirection: 'column', gap: 3,
              padding: '10px 14px', background: T.btnHov,
              border: `1px solid ${T.ctrlBdr}`, borderRadius: 3,
              textDecoration: 'none', minWidth: 140,
              transition: 'border-color 0.15s',
            }}
          >
            <span style={{ fontFamily: NU, fontSize: 8, color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{c.role}</span>
            <span style={{ fontFamily: GD, fontSize: 15, color: T.text, fontStyle: 'italic' }}>{c.vendorName}</span>
            {c.category && <span style={{ fontFamily: NU, fontSize: 9, color: T.muted }}>{c.category}</span>}
          </a>
        ))}
      </div>
    </div>
  );
}

// ── In-reader enquiry modal ───────────────────────────────────────────────────
function InReaderEnquiryModal({ hotspot, onClose }) {
  const [form,    setForm]    = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      const { supabase } = await import('../lib/supabaseClient');
      await supabase.from('inquiries').insert({
        vendor_name: hotspot.vendorName,
        vendor_url:  hotspot.url,
        category:    hotspot.category,
        name:        form.name,
        email:       form.email,
        message:     form.message || `Enquiry about ${hotspot.label}`,
        source:      hotspot.source || 'publication_hotspot',
      }).catch(() => {});
      setSent(true);
    } catch {}
    setSending(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#0A0908', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, padding: 32, width: 'min(420px,90vw)', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 18 }}>✕</button>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
            <div style={{ fontFamily: GD, fontSize: 22, color: '#F0EBE0', marginBottom: 8 }}>Enquiry sent</div>
            <div style={{ fontFamily: NU, fontSize: 12, color: MUTED }}>We'll be in touch shortly.</div>
            <button onClick={onClose} style={{ marginTop: 20, fontFamily: NU, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GOLD, background: 'none', border: `1px solid ${GOLD}`, padding: '9px 24px', borderRadius: 2, cursor: 'pointer' }}>Close</button>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: NU, fontSize: 9, color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Enquire About</div>
            <div style={{ fontFamily: GD, fontSize: 22, color: '#F0EBE0', fontStyle: 'italic', marginBottom: 4 }}>{hotspot.label}</div>
            {hotspot.vendorName && <div style={{ fontFamily: NU, fontSize: 12, color: MUTED, marginBottom: 20 }}>{hotspot.vendorName}</div>}
            {[
              { key: 'name',  label: 'Your Name',      placeholder: 'Isabella Chen',          type: 'text'  },
              { key: 'email', label: 'Email Address',   placeholder: 'isabella@example.com',   type: 'email' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>{f.label}</label>
                <input
                  type={f.type}
                  value={form[f.key]}
                  onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 3, color: '#fff', fontFamily: NU, fontSize: 13, padding: '8px 10px', outline: 'none' }}
                />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>Message (optional)</label>
              <textarea
                value={form.message}
                onChange={e => setForm(v => ({ ...v, message: e.target.value }))}
                rows={3}
                placeholder={`I'm interested in ${hotspot.label}…`}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 3, color: '#fff', fontFamily: NU, fontSize: 13, padding: '8px 10px', outline: 'none', resize: 'vertical' }}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={sending || !form.name || !form.email}
              style={{ width: '100%', background: GOLD, border: 'none', color: '#0A0908', fontFamily: NU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '12px', borderRadius: 2, cursor: 'pointer', opacity: (!form.name || !form.email) ? 0.5 : 1 }}
            >
              {sending ? 'Sending…' : 'Send Enquiry ✦'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Film lightbox (magazine "Watch Film") ───────────────────────────────────
function FilmModal({ url, onClose }) {
  if (!url) return null;
  const isVimeo = url.includes('vimeo.com');
  const ytId = !isVimeo ? (url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([^&?/]+)/)?.[1]) : null;
  const vmId = isVimeo ? (url.match(/vimeo\.com\/(\d+)/)?.[1]) : null;
  const embed = isVimeo
    ? `https://player.vimeo.com/video/${vmId}?autoplay=1`
    : `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 650, background: 'rgba(0,0,0,0.93)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 22, background: 'none', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer', lineHeight: 1 }}>✕</button>
      <div style={{ width: 'min(1100px, 92vw)', aspectRatio: '16 / 9', boxShadow: '0 20px 80px rgba(0,0,0,0.6)' }}>
        <iframe src={embed} title="Wedding film" allow="autoplay; encrypted-media; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />
      </div>
    </div>
  );
}

// ── Embedded page video (plays INLINE on a specific flipbook page) ───────────
// Anchors an iframe over the live [data-page-number] node so the film reads as
// the page itself. Tracks the node through flips/resize/zoom via rAF; only shown
// once the flip has settled on that page (active).
// region = fraction of the page the film occupies (defaults to the editorial/feature
// template's full-bleed top image: full width, top 730/1754 = 41.62% of the page).
function EmbeddedPageVideo({ pageNumber, url, active, region = { x: 0, y: 0, w: 100, h: 41.62 } }) {
  const [rect, setRect] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { if (!active) setPlaying(false); }, [active]);
  useEffect(() => {
    if (!active) { setRect(null); return; }
    let raf;
    const tick = () => {
      const node = document.querySelector(`[data-page-number="${pageNumber}"]`);
      if (node) {
        const r = node.getBoundingClientRect();
        if (r.width > 40 && r.height > 40) {
          setRect(prev => (prev && Math.abs(prev.left - r.left) < 0.5 && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.width - r.width) < 0.5)
            ? prev : { left: r.left, top: r.top, width: r.width, height: r.height });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [active, pageNumber]);
  if (!active || !rect) return null;
  // Region within the page (the image slot). The rendered page photo is the poster.
  const regW = rect.width  * region.w / 100;
  const regH = rect.height * region.h / 100;
  const regL = rect.left   + rect.width  * region.x / 100;
  const regT = rect.top    + rect.height * region.y / 100;
  // NOT playing → tasteful play button over the page photo (no autoplay).
  if (!playing) {
    return createPortal(
      <button onClick={() => setPlaying(true)} aria-label="Watch the film"
        style={{ position: 'fixed', left: regL, top: regT, width: regW, height: regH, zIndex: 116, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
        <span style={{ width: 78, height: 78, borderRadius: '50%', background: 'rgba(10,9,8,0.42)', border: `1.5px solid ${GOLD}`, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
          <span style={{ width: 0, height: 0, borderTop: '11px solid transparent', borderBottom: '11px solid transparent', borderLeft: `18px solid ${GOLD}`, marginLeft: 5 }} />
        </span>
        <span style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: 'rgba(10,9,8,0.42)', padding: '5px 12px', borderRadius: 2, backdropFilter: 'blur(2px)' }}>Watch Film</span>
      </button>,
      document.body
    );
  }
  // Playing → film WITH SOUND + controls (user-initiated, so audio is allowed).
  const isVimeo = url.includes('vimeo.com');
  const ytId = !isVimeo ? (url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([^&?/]+)/)?.[1]) : null;
  const vmId = isVimeo ? (url.match(/vimeo\.com\/(\d+)/)?.[1]) : null;
  const embed = isVimeo
    ? `https://player.vimeo.com/video/${vmId}?autoplay=1`
    : `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&controls=1`;
  return createPortal(
    <div style={{ position: 'fixed', left: regL, top: regT, width: regW, height: regH, zIndex: 116, overflow: 'hidden', background: '#000' }}>
      <iframe
        src={embed} title="Wedding film" allow="autoplay; encrypted-media; fullscreen" allowFullScreen
        style={{ width: '100%', height: '100%', border: 'none' }}
      />
    </div>,
    document.body
  );
}

// ── Back-cover contact button, a clickable region anchored over the BAKED CTA on
// the rendered back cover (so the PDF carries the printed CTA + enquiry URL, and
// the reader makes it clickable + tracked).
//
// The position comes from the PAGE, as a link_target of type 'cta' carrying x/y/w/h
// percentages. It used to be this constant alone, applied to all 368 publications.
// That is the geometry of the gold bar _gen_magazine bakes into its back cover, so
// on a generated venue publication it is exactly right, but a third of publications
// were built from other templates and have no bar, and there the button simply
// landed at 58% on top of whatever was printed. On Ferdinand Atelier it covered the
// standfirst. Every back cover was measured and now carries its own box
// (_fix_backcover_cta.mjs), and _gen_magazine writes one for anything new, so the
// constant is only a floor for a page that somehow has neither.
const CTA_BOX = { x: 29.03, y: 58.04, w: 41.94, h: 4.05 };
function BackCoverContact({ pageNumber, onClick, box }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let raf;
    const tick = () => {
      const node = document.querySelector(`[data-page-number="${pageNumber}"]`);
      if (node) {
        const r = node.getBoundingClientRect();
        if (r.width > 40 && r.height > 40) {
          setRect(p => (p && Math.abs(p.left - r.left) < 0.5 && Math.abs(p.top - r.top) < 0.5 && Math.abs(p.width - r.width) < 0.5)
            ? p : { left: r.left, top: r.top, width: r.width, height: r.height });
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [pageNumber]);
  if (!rect) return null;
  const g = box || CTA_BOX;
  const l = rect.left + rect.width * (g.x / 100), t = rect.top + rect.height * (g.y / 100);
  const w = rect.width * (g.w / 100), h = rect.height * (g.h / 100);
  // A real, opaque gold button rendered exactly over the baked CTA (which stays in the
  // rendered page for the PDF). Opens the tracked enquiry form, never navigates.
  return createPortal(
    <button onClick={onClick} aria-label="Contact us"
      onMouseEnter={e => { e.currentTarget.style.background = '#876A38'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#9A7B43'; }}
      style={{ position: 'fixed', left: l, top: t, width: w, height: h, zIndex: 120,
        background: '#9A7B43', color: '#FBF7EF', border: 'none', borderRadius: 3, cursor: 'pointer',
        fontFamily: NU, fontWeight: 700, letterSpacing: '0.24em', textIndent: '0.24em', textTransform: 'uppercase',
        fontSize: Math.max(8, h * 0.27), display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)', transition: 'background 160ms' }}
    >Contact Us</button>,
    document.body
  );
}

// ── Rotate prompt (portrait mobile) ─────────────────────────────────────────
function RotatePrompt({ onBack, T }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: T?.bg || '#080706',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 28, padding: 40, textAlign: 'center',
    }}>
      {/* Rotate icon */}
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ opacity: 0.55 }}>
        <rect x="14" y="8" width="36" height="48" rx="4" stroke="#d39a21" strokeWidth="2"/>
        <path d="M28 56h8" stroke="#d39a21" strokeWidth="2" strokeLinecap="round"/>
        <path d="M42 28 C 52 28, 56 36, 52 44" stroke="#d39a21" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3"/>
        <path d="M52 44 L56 40 M52 44 L48 40" stroke="#d39a21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <div>
        <div style={{
          fontFamily: GD, fontStyle: 'italic', fontSize: 26,
          color: '#F0EBE0', marginBottom: 12, lineHeight: 1.3,
        }}>
          Rotate your device
        </div>
        <p style={{
          fontFamily: NU, fontSize: 14, lineHeight: 1.65,
          color: 'rgba(255,255,255,0.5)', maxWidth: 260, margin: '0 auto',
        }}>
          Turn your phone to landscape for the full reading experience
        </p>
      </div>
      <button
        onClick={onBack}
        style={{
          fontFamily: NU, fontSize: 11, fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: GOLD, background: 'none',
          border: `1px solid ${GOLD}`, padding: '10px 28px',
          borderRadius: 1, cursor: 'pointer', marginTop: 8,
        }}
      >
        Back
      </button>
    </div>
  );
}

// ── Main reader component ─────────────────────────────────────────────────────
export default function PublicationsReaderPage({ slug, onBack, isPreview: isPreviewProp = false, embedded = false }) {
  // Admin bypass, skip email gate and allow draft preview if logged in as admin
  const { isAuthenticated: isAdminUser } = useAdminAuth();

  // ?preview=1 via URL requires an authenticated admin session.
  // The isPreviewProp path (admin React navigation) is always trusted, it is
  // set by admin code, not by a URL parameter anyone can append.
  const urlPreviewRequested = new URLSearchParams(window.location.search).get('preview') === '1';
  const isPreview = isPreviewProp || (urlPreviewRequested && isAdminUser);

  // Fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenEnabled) return;
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  }, []);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const [issue,   setIssue]   = useState(null);
  // Companion readable, hyperlinked TEXT EDITION of this issue (a magazine_posts
  // article sharing the issue's slug). The flip pages are rasterised images, so the
  // text edition is where the words can actually be read + followed as links + indexed.
  const [textEditionSlug, setTextEditionSlug] = useState(null);
  const [pages,   setPages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [isDesktop,   setIsDesktop]   = useState(() => window.innerWidth >= 900);

  // ── Tier 1 state ─────────────────────────────────────────────────────────────
  const [zoom,           setZoom]           = useState(1);
  const [panX,           setPanX]           = useState(0);
  const [panY,           setPanY]           = useState(0);
  // Lights on by default: a magazine reads as ink on paper, and the light theme
  // (#EAE5DC surround, white page) is what a reader expects to open into. Dark
  // stays one tap away via the Lights Off control, but is no longer the entry
  // state. Not persisted, so this is genuinely what every reader opens to.
  const [readerMode,     setReaderMode]     = useState('light');
  const [showTOC,        setShowTOC]        = useState(false);
  const [bookmarks,      setBookmarks]      = useState([]);
  const [showIntro,      setShowIntro]      = useState(false);
  const [toastVisible,   setToastVisible]   = useState(false);
  const [showExportModal,setShowExportModal] = useState(false);
  const toastTimer = useRef(null);

  // ── Tier 2: hotspots + credits state ─────────────────────────────────────────
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [creditsOpen,   setCreditsOpen]   = useState(false);
  const [filmOpen,      setFilmOpen]      = useState(false); // magazine "Watch Film" lightbox
  const [enquiryOpen,   setEnquiryOpen]   = useState(false); // back-cover "Contact the Venue" -> standard LuxeEnquiryModal
  const [venueForEnquiry, setVenueForEnquiry] = useState(null); // listing object fetched by slug

  // Does this issue belong to a member? A member publication's slug IS its listing
  // slug (the convention vendorAccountsService relies on), and only those have the
  // CTA baked into the back cover. Editorial and collection issues do not, so the
  // clickable region used to float over whatever happened to be on their last page.
  const [issueHasListing, setIssueHasListing] = useState(false);
  useEffect(() => {
    if (!issue?.slug) { setIssueHasListing(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('../lib/supabaseClient');
        const { data } = await supabase.from('listings')
          .select('id').eq('slug', issue.slug).maybeSingle();
        if (!cancelled) setIssueHasListing(!!data);
      } catch { if (!cancelled) setIssueHasListing(false); }
    })();
    return () => { cancelled = true; };
  }, [issue?.slug]);

  // Open the SAME standard enquiry form used on cards/profiles (LuxeEnquiryModal),
  // resolving the venue listing by the issue slug so the lead attributes correctly.
  const openVenueEnquiry = useCallback(async () => {
    if (venueForEnquiry) { setEnquiryOpen(true); return; }
    let v = { id: issue?.slug, name: issue?.title };
    try {
      const { supabase } = await import('../lib/supabaseClient');
      const { data } = await supabase.from('listings')
        .select('id,name,slug,city,region,country,hero_image,card_image')
        .eq('slug', issue?.slug).maybeSingle();
      if (data) v = { id: data.id, name: data.name, city: data.city, region: data.region, country: data.country, heroImage: data.hero_image || data.card_image, image: data.hero_image || data.card_image };
    } catch {}
    setVenueForEnquiry(v);
    setEnquiryOpen(true);
  }, [venueForEnquiry, issue?.slug, issue?.title]);

  // Where the back-cover button actually goes.
  //
  // A member publication's slug IS a listing slug, so the venue enquiry form is
  // right there and the lead attributes to them.
  //
  // Anything else (a membership edition, a collection, an editorial issue) has no
  // venue to enquire about. That form cannot be reused for those: it asks for two
  // first names, a wedding date and a guest count, so pointing a bridal atelier at
  // it would ask them when their wedding is. It opens the reader's own mail client
  // instead, addressed to us and subject-lined with the issue, so the reply
  // arrives knowing who it is from. The address is printed on the page too, which
  // covers anyone with no mail client configured.
  const openBackCoverContact = useCallback(() => {
    if (issueHasListing) { openVenueEnquiry(); return; }
    const subject = encodeURIComponent(`Membership enquiry: ${issue?.title || 'Luxury Wedding Directory'}`);
    window.location.href = `mailto:info@5starweddingdirectory.com?subject=${subject}`;
  }, [issueHasListing, openVenueEnquiry, issue?.title]);

  // ── Tier 6: search + text mode state ─────────────────────────────────────────
  const [showSearch,      setShowSearch]      = useState(false);
  const [showTextMode,    setShowTextMode]     = useState(false);
  const [showSharePopover, setShowSharePopover] = useState(false);

  // ── Reactions state ───────────────────────────────────────────────────────────
  const [reactionCounts, setReactionCounts] = useState({}); // { pageNum: count }
  const [myReactions,    setMyReactions]    = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`lwd_reactions_${slug}`) || '[]')); }
    catch { return new Set(); }
  });

  // ── Flipbook imperative handle (react-pageflip) ──────────────────────────────
  // Owned by HTMLFlipBook; we call `.pageFlip().flipNext()/flipPrev()/turnToPage()`
  // to drive transitions. Library fires onFlip → syncs currentPage back. The
  // syncingRef guards against feedback loops when external state changes
  // (TOC jumps, URL sync) trigger turnToPage which itself fires onFlip.
  const flipBookRef = useRef(null);
  const syncingRef  = useRef(false);

  // ── Email gate (Phase 3) ─────────────────────────────────────────────────────
  // Admin users bypass the gate entirely, they're already in the system.
  const [gateUnlocked, setGateUnlocked] = useState(() => {
    if (isAdminUser || embedded) return true; // embedded (in-listing modal) reads freely, the listing has its own enquiry CTAs
    try { return typeof window !== 'undefined' && localStorage.getItem(GATE_STORAGE_KEY) === '1'; }
    catch { return false; }
  });
  const [gateOpen,     setGateOpen]     = useState(false);
  const [gateReason,   setGateReason]   = useState('page_threshold'); // page_threshold | time_fallback | intent_nav | intent_rapid
  const gateTriggeredRef = useRef(false); // fires once per session until unlock/skip
  const rapidClicksRef   = useRef([]);    // timestamps of recent next-clicks for rapid-click detection

  // Drag-to-pan refs
  const dragging       = useRef(false);
  const dragStartX     = useRef(0);
  const dragStartY     = useRef(0);
  const dragPanStartX  = useRef(0);
  const dragPanStartY  = useRef(0);

  // Pinch refs
  const pinchActive   = useRef(false);
  const initialDist   = useRef(0);
  const initialZoom   = useRef(1);
  const touchStartX   = useRef(null);
  const touchStartY   = useRef(null);

  // Analytics
  const pageEnteredAt = useRef(null);
  const prevPageRef   = useRef(null);

  // Read events tracking, crypto.randomUUID() requires iOS 15.4+ / Chrome 92+.
  // Fall back to Math.random for older mobile browsers so the component never
  // crashes on mount (which would prevent URL sync and show a black screen).
  const sessionId      = useRef(
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        })
  );
  const mountTimeRef   = useRef(Date.now());

  // Personalised cover overlay
  const [personalisedData,     setPersonalisedData]     = useState(null);
  const [showPersonalisedCover, setShowPersonalisedCover] = useState(false);

  // Theme derivation
  const T      = READER_THEMES[readerMode] || READER_THEMES.dark;
  const BG     = T.bg;
  const PAGE_BG = T.pageBg;

  // ── Responsive ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Load data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) {
      setError('No issue specified. Go back and try again.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true); setError(null); setCurrentPage(1);

    (async () => {
      const isPersonalised = new URLSearchParams(window.location.search).get('personalised') === 'true';

      // Public readers use the status-filtered fetch so draft content is never
      // returned at the DB layer. Admin preview uses the unfiltered fetch.
      const { data: issueData, error: issErr } = isPreview
        ? await fetchIssueBySlug(slug)
        : await fetchPublishedIssueBySlug(slug);
      if (cancelled) return;
      if (issErr || !issueData) {
        // ── Personalised issue fallback ──────────────────────────────────────
        // Personalised slugs live in magazine_personalised_issues, not magazine_issues.
        // When ?personalised=true, load the parent issue + override page 1 with
        // the couple's custom cover image.
        if (isPersonalised) {
          const { data: pi } = await supabase
            .from('magazine_personalised_issues')
            .select('*, magazine_issues(*)')
            .eq('slug', slug)
            .single();
          if (!cancelled && pi?.magazine_issues) {
            const parentIssue = pi.magazine_issues;
            if (!isPreview && parentIssue.status !== 'published') {
              setError(urlPreviewRequested && !isAdminUser
                ? 'Preview requires an admin session.'
                : 'This issue is not yet published.');
              setLoading(false);
              return;
            }
            setIssue(parentIssue);
            const { data: pagesData } = await fetchPages(parentIssue.id);
            if (cancelled) return;
            // Override page 1 image_url with the couple's personalised cover,
            // and cache-bust every other page's storage URLs to beat the
            // year-long `cacheControl: 31536000` applied by upload-magazine-page.
            const allPages = (pagesData || []).map((p, i) => {
              if (i === 0 && pi.cover_url) return { ...p, image_url: pi.cover_url };
              const ck = buildCacheKey(p.updated_at);
              return {
                ...p,
                image_url:     bustUrl(p.image_url,     ck),
                thumbnail_url: bustUrl(p.thumbnail_url, ck),
              };
            });
            setPages(allPages);
            setBookmarks(loadBookmarks(parentIssue.id));
            // Trigger the cinematic personalised overlay (names, date, venue)
            setPersonalisedData(pi);
            setShowPersonalisedCover(true);
            setLoading(false);
            return;
          }
        }
        setError('Issue not found. It may have been unpublished.');
        setLoading(false);
        return;
      }

      // ── Status gate: only published issues are visible to public ─────────────
      console.log('[reader] issue loaded:', {
        id:              issueData.id,
        slug:            issueData.slug,
        status:          issueData.status,
        render_version:  issueData.render_version,
        processing_state: issueData.processing_state,
        isPreview:       isPreview,
      });
      if (!isPreview && issueData.status !== 'published') {
        setError(urlPreviewRequested && !isAdminUser
          ? 'Preview requires an admin session.'
          : 'This issue is not yet published.');
        setLoading(false);
        return;
      }

      setIssue(issueData);

      // Resolve the companion text edition (a published article sharing this slug).
      // Non-blocking, the flip reader works with or without it.
      supabase.from('magazine_posts')
        .select('slug').eq('slug', issueData.slug).eq('published', true).maybeSingle()
        .then(({ data: te }) => { if (!cancelled && te?.slug) setTextEditionSlug(te.slug); })
        .catch(() => {});

      const { data: pagesData } = await fetchPages(issueData.id);
      if (cancelled) return;
      console.log('[reader] pages loaded:', (pagesData || []).length, (pagesData || []).map(p => ({
        n:        p.page_number,
        hasImage: !!p.image_url,
        url:      p.image_url ? p.image_url.slice(0, 80) : null,
      })));
      // Cache-bust every storage URL so stale blank uploads (locked in by
      // upload-magazine-page's year-long cacheControl) are defeated.
      const bustedPages = (pagesData || []).map(p => {
        const ck = buildCacheKey(p.updated_at);
        return {
          ...p,
          image_url:     bustUrl(p.image_url,     ck),
          thumbnail_url: bustUrl(p.thumbnail_url, ck),
        };
      });
      setPages(bustedPages);
      setLoading(false);

      // Load bookmarks
      setBookmarks(loadBookmarks(issueData.id));

      // Cinematic intro, skip on mobile (<640px). The user already saw
      // the cover on the publications page; showing it again as a full-
      // screen overlay before they can read adds friction on small screens.
      const introKey     = `lwd_intro_${issueData.id}`;
      const isMobileView = typeof window !== 'undefined' && window.innerWidth < 640;
      if (!isMobileView && !sessionStorage.getItem(introKey)) {
        setShowIntro(true);
      }

      // URL sync: read page param
      const sp = new URLSearchParams(window.location.search);
      const pageParam = sp.get('page');
      if (pageParam) {
        const n = parseInt(pageParam, 10);
        if (!isNaN(n) && n >= 1) {
          setCurrentPage(Math.min(n, (pagesData || []).length));
        }
      }

      trackIssueView(issueData.id, window.innerWidth >= 900 ? 'spread' : 'single');
      pageEnteredAt.current = Date.now();
      prevPageRef.current = 1;

      // Scroll to top of document when arriving from a magazine link.
      // The reader is position:fixed inset:0 so this is mostly a hygiene
      // step (in case a parent page had scrolled), but also ensures
      // mobile browsers don't open the reader with an offset scroll.
      if (typeof window !== 'undefined') {
        try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch { window.scrollTo(0, 0); }
      }

      // Check for personalised cover
      const sp2 = new URLSearchParams(window.location.search);
      if (sp2.get('personalised') === 'true') {
        // Look up personalised issue by slug (current URL slug)
        const { data: persData } = await supabase
          .from('magazine_personalised_issues')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();
        if (!cancelled && persData) {
          setPersonalisedData(persData);
          setShowPersonalisedCover(true);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [slug, isAdminUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reactions: load counts from analytics table ──────────────────────────────
  useEffect(() => {
    if (!issue?.id) return;
    supabase
      .from('magazine_analytics')
      .select('page_number')
      .eq('issue_id', issue.id)
      .eq('event_type', 'reaction')
      .then(({ data }) => {
        if (!data) return;
        const counts = {};
        data.forEach(r => {
          if (r.page_number != null) counts[r.page_number] = (counts[r.page_number] || 0) + 1;
        });
        setReactionCounts(counts);
      });
  }, [issue?.id]);

  // ── Structured data (JSON-LD) for this specific issue ────────────────────────
  useEffect(() => {
    if (!issue) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "PublicationIssue",
      "name": issue.title || "Luxury Wedding Directory Magazine",
      "url": `https://luxuryweddingdirectory.com/publications/${issue.slug}`,
      "issueNumber": issue.issue_number?.toString(),
      "datePublished": issue.published_at,
      "image": issue.cover_image ? imgUrl(issue.cover_image, { width: 1200, resize: 'contain' }) : undefined,
      "description": issue.intro || issue.excerpt || undefined,
      "isPartOf": {
        "@type": "Periodical",
        "name": "Luxury Wedding Directory Magazine",
        "url": "https://luxuryweddingdirectory.com/publications",
        "publisher": {
          "@type": "Organization",
          "name": "Luxury Wedding Directory",
          "url": "https://luxuryweddingdirectory.com"
        }
      },
      "pageCount": issue.page_count || undefined,
    };

    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "issue-schema";
    el.textContent = JSON.stringify(schema, (_, v) => v === undefined ? undefined : v);
    document.getElementById("issue-schema")?.remove();
    document.head.appendChild(el);

    return () => {
      document.getElementById("issue-schema")?.remove();
    };
  }, [issue?.id]);

  // ── Device / browser / referrer helpers (Feature 11) ────────────────────────
  const _referrer = typeof document !== 'undefined' ? (document.referrer || 'direct') : 'direct';
  const _device   = typeof window   !== 'undefined'
    ? (window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop')
    : 'desktop';
  function _browser() {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent;
    if (ua.includes('Edg/'))    return 'edge';
    if (ua.includes('Chrome/')) return 'chrome';
    if (ua.includes('Firefox/'))return 'firefox';
    if (ua.includes('Safari/')) return 'safari';
    return 'other';
  }
  const _browserName = _browser();

  // ── Geography: detect country via ipapi.co ───────────────────────────────────
  async function getCountry() {
    try {
      const r = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
      const d = await r.json(); return d.country_name || null;
    } catch { return null; }
  }

  // ── Read events: issue_open on mount, issue_close on unmount ─────────────────
  useEffect(() => {
    if (!issue?.id) return;
    const sid = sessionId.current;
    mountTimeRef.current = Date.now();
    // Fire-and-forget issue_open (with country detection)
    getCountry().then(country => {
      supabase.from('magazine_read_events').insert({
        issue_id: issue.id, session_id: sid, event_type: 'issue_open', page_number: 1,
        referrer: _referrer, device: _device, browser: _browserName,
        ...(country ? { country } : {}),
      });
    });
    return () => {
      // Fire-and-forget issue_close
      supabase.from('magazine_read_events').insert({
        issue_id: issue.id, session_id: sid, event_type: 'issue_close',
        duration_ms: Date.now() - mountTimeRef.current,
        referrer: _referrer, device: _device, browser: _browserName,
      });
    };
  }, [issue?.id]);

  // ── Read events: page_view on page change ─────────────────────────────────────
  useEffect(() => {
    if (!issue?.id || !currentPage) return;
    supabase.from('magazine_read_events').insert({
      issue_id: issue.id, session_id: sessionId.current, event_type: 'page_view', page_number: currentPage,
      referrer: _referrer, device: _device, browser: _browserName,
    });
  }, [issue?.id, currentPage]);

  // ── Document title for this issue ────────────────────────────────────────────
  useEffect(() => {
    if (!issue) return;
    const parts = [issue.title, "The Magazine", "Luxury Wedding Directory"].filter(Boolean);
    document.title = parts.join(" · ");
    return () => { document.title = "Luxury Wedding Directory"; };
  }, [issue?.title]);

  // ── Reactions handler ────────────────────────────────────────────────────────
  const handleReaction = useCallback((pageNum) => {
    if (!issue?.id) return;
    const already = myReactions.has(pageNum);
    if (already) return; // one reaction per page per session
    // Optimistic update
    setReactionCounts(prev => ({ ...prev, [pageNum]: (prev[pageNum] || 0) + 1 }));
    const next = new Set(myReactions);
    next.add(pageNum);
    setMyReactions(next);
    try { localStorage.setItem(`lwd_reactions_${slug}`, JSON.stringify([...next])); } catch {}
    // Fire-and-forget to analytics
    const sessionId = (() => {
      try { return sessionStorage.getItem('lwd_pub_session') || undefined; } catch { return undefined; }
    })();
    supabase.from('magazine_analytics').insert({
      issue_id:    issue.id,
      page_number: pageNum,
      event_type:  'reaction',
      session_id:  sessionId,
      device_type: window.innerWidth < 640 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
      created_at:  new Date().toISOString(),
    }).then(() => {}).catch(() => {});
  }, [issue?.id, myReactions, slug]);

  // ── Spread mode: declared here, before Helpers, because step/canNext reference it
  //
  // Tablet/desktop (≥640px) → double-spread layout, paper-curl flip
  // Mobile (<640px)         → single-page portrait flipbook (still flips)
  //
  // Forcing double-spread on small viewports caused (a) a black-screen
  // render in react-pageflip's edge cases and (b) an unreadable
  // cramped layout that needed pinch-zoom to read. The luxury feel on
  // mobile is the flip animation itself, not squeezing a desktop
  // spread onto a phone screen. Vogue / Harper's / Net-a-Porter all
  // ship portrait single-page on mobile for the same reason.
  //
  // viewportWide is held in state and refreshed on resize +
  // orientationchange so rotating from portrait to landscape (or
  // resizing DevTools responsive mode) re-evaluates the layout
  // without a full reload.
  const [viewportWide, setViewportWide] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 640
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const recompute = () => setViewportWide(window.innerWidth >= 640);
    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', recompute);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', recompute);
    };
  }, []);

  // ── Auto-rotate to landscape when reader loads in portrait ───────────────────
  // On Android Chrome: requestFullscreen → screen.orientation.lock('landscape').
  // On iOS Safari: both APIs throw, the rotate prompt shows as a fallback.
  // Attempted once per mount; orientation lock is released on unmount.
  const autoRotateAttempted = useRef(false);
  useEffect(() => {
    if (loading || !pages.length || viewportWide || autoRotateAttempted.current) return;
    autoRotateAttempted.current = true;
    (async () => {
      try {
        if (document.fullscreenEnabled && !document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
          setIsFullscreen(true);
        }
        if (typeof screen?.orientation?.lock === 'function') {
          await screen.orientation.lock('landscape');
        }
      } catch {
        // Unsupported (iOS) or permission denied, rotate prompt remains visible
      }
    })();
  }, [loading, pages.length, viewportWide]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release orientation lock when the reader closes
  useEffect(() => () => {
    try { screen?.orientation?.unlock?.(); } catch {}
  }, []);

  const forceSpreadEarly   = issue?.spread_layout !== 'single';
  const useDoubleSpread    = forceSpreadEarly && viewportWide;

  // Diagnostic, check console if the reader isn't showing a 2-page spread:
  useEffect(() => {
    if (issue) {
      console.log('[reader] layout:', {
        isDesktop,
        viewportWidth:  typeof window !== 'undefined' ? window.innerWidth : null,
        spread_layout:  issue.spread_layout ?? '(null → defaults to double)',
        useDoubleSpread,
      });
    }
  }, [issue, isDesktop, useDoubleSpread]);

  // ── Paywall gate: declared here too so goNext (below) can reference it ───────
  const paywallBlocking    = (issue?.paywall_enabled === true) && currentPage > (issue?.free_page_count ?? 3);

  // ── Email gate: intent trigger helper, declared BEFORE goNext to avoid TDZ ──
  // Called by high-intent actions (TOC open, thumbnail jump, rapid clicking).
  // No-op if gate already triggered, unlocked, or loading.
  const triggerGateByIntent = useCallback((reason) => {
    if (gateUnlocked || gateOpen || gateTriggeredRef.current) return;
    if (!issue || loading) return;
    gateTriggeredRef.current = true;
    setGateReason(reason);
    setGateOpen(true);
  }, [gateUnlocked, gateOpen, issue, loading]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const totalPages = pages.length;
  const step = useDoubleSpread ? 2 : 1;
  const canPrev = currentPage > 1;
  const canNext = currentPage + (useDoubleSpread ? 1 : 0) < totalPages;

  const resetZoom = useCallback(() => {
    setZoom(1); setPanX(0); setPanY(0);
  }, []);

  const firePageTurn = useCallback((newPage) => {
    if (!issue) return;
    const now   = Date.now();
    const dwell = pageEnteredAt.current ? now - pageEnteredAt.current : 0;
    const prev  = prevPageRef.current;
    trackPageTurn(issue.id, newPage, isDesktop ? 'spread' : 'single', dwell, prev);
    pageEnteredAt.current = now;
    prevPageRef.current   = newPage;
  }, [issue, isDesktop]);

  // ── Imperative API now lives on LuxuryFlipBook directly. The previous
  // `flipBookRef.current.pageFlip().turnToPage/flipPrev/flipNext` chain
  // (react-pageflip's awkward double-call API) is replaced with the new
  // wrapper's clean `flipBookRef.current.turnTo(n)` / `.flipPrev()` /
  // `.flipNext()` methods. Same semantics, simpler call sites.
  const goToPage = useCallback((n) => {
    const clamped = Math.max(1, Math.min(totalPages, n));
    resetZoom();
    const fb = flipBookRef.current;
    if (fb && typeof fb.turnTo === 'function') {
      try {
        syncingRef.current = true;
        fb.turnTo(clamped);
        // onFlip will update state; flag clears there.
        return;
      } catch { /* fall through to direct state update */ }
    }
    // Fallback: engine not mounted yet (initial load / error state).
    firePageTurn(clamped);
    setCurrentPage(clamped);
  }, [totalPages, firePageTurn, resetZoom]);

  const goPrev = useCallback(() => {
    if (!canPrev) return;
    if (gateOpen && !gateUnlocked) return; // block nav while email gate is showing
    if (zoom > 1) { goToPage(Math.max(1, currentPage - step)); return; }
    const fb = flipBookRef.current;
    if (fb && typeof fb.flipPrev === 'function') {
      try {
        fb.flipPrev();
        // Optimistic React-state update. Page-flip 2.x's `flip` event
        // is unreliable when returning to the cover (often fires with
        // stale e.data or not at all), which leaves UI bound to
        // `currentPage`, like the cover-centring transform, stuck on
        // the previous value. Predict the new page locally; the engine's
        // own onFlip will reconcile if it fires later.
        const predicted = Math.max(1, currentPage - step);
        if (predicted !== currentPage) setCurrentPage(predicted);
        return;
      } catch { /* fall through */ }
    }
    goToPage(Math.max(1, currentPage - step));
  }, [canPrev, currentPage, step, goToPage, zoom, gateOpen, gateUnlocked]);

  const goNext = useCallback(() => {
    if (!canNext || paywallBlocking) return;
    if (gateOpen && !gateUnlocked) return; // block nav while email gate is showing

    // Intent trigger: 4+ next-clicks within 3 seconds = rapid skim = high intent
    if (!gateUnlocked && !gateTriggeredRef.current) {
      const now = Date.now();
      rapidClicksRef.current = [...rapidClicksRef.current, now].filter(t => now - t <= 3000);
      if (rapidClicksRef.current.length >= 4) {
        rapidClicksRef.current = [];
        triggerGateByIntent('intent_rapid');
        return;
      }
    }
    if (zoom > 1) { goToPage(Math.min(totalPages, currentPage + step)); return; }
    const fb = flipBookRef.current;
    if (fb && typeof fb.flipNext === 'function') {
      try { fb.flipNext(); return; } catch { /* fall through */ }
    }
    goToPage(Math.min(totalPages, currentPage + step));
  }, [canNext, paywallBlocking, currentPage, step, totalPages, goToPage, zoom, gateOpen, gateUnlocked, triggerGateByIntent]);

  // ── URL sync on page change (debounced 200ms) ────────────────────────────────
  // Debounced to avoid spamming history entries during rapid keyboard navigation.
  // Also eliminates the double-fire that occurred when goToPage called replaceState
  // directly AND this effect ran on the same currentPage state change.
  useEffect(() => {
    if (!slug || !issue || embedded) return; // embedded modal must not rewrite the listing URL
    const t = setTimeout(() => {
      window.history.replaceState({}, '', `/publications/${slug}?page=${currentPage}`);
    }, 200);
    return () => clearTimeout(t);
  }, [currentPage, slug, issue, embedded]);

  // ── Close credits drawer on page navigation ───────────────────────────────────
  useEffect(() => {
    setCreditsOpen(false);
  }, [currentPage]);

  // ── Email gate: page-threshold trigger ───────────────────────────────────────
  // Opens after user reaches GATE_PAGE_TRIGGER. Fires once.
  useEffect(() => {
    if (gateUnlocked || gateOpen || gateTriggeredRef.current) return;
    if (!issue || loading) return;
    if (currentPage >= GATE_PAGE_TRIGGER) {
      gateTriggeredRef.current = true;
      setGateReason('page_threshold');
      setGateOpen(true);
    }
  }, [currentPage, gateUnlocked, gateOpen, issue, loading]);

  // ── Email gate: time fallback trigger ────────────────────────────────────────
  // If user lingers early in the issue without reaching page 3, open after 25s.
  useEffect(() => {
    if (gateUnlocked || gateOpen || gateTriggeredRef.current) return;
    if (!issue || loading) return;
    const t = setTimeout(() => {
      if (gateUnlocked || gateOpen || gateTriggeredRef.current) return;
      gateTriggeredRef.current = true;
      setGateReason('time_fallback');
      setGateOpen(true);
    }, GATE_TIME_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [issue, loading, gateUnlocked, gateOpen]);

  // ── Email gate handlers ──────────────────────────────────────────────────────
  const handleGateUnlock = useCallback(() => {
    try { localStorage.setItem(GATE_STORAGE_KEY, '1'); } catch { /* ignore */ }
    setGateUnlocked(true);
    setGateOpen(false);
  }, []);

  const handleGateSkip = useCallback(() => {
    // Soft close for this session only, no localStorage write.
    // Won't re-open again this session (gateTriggeredRef stays true).
    setGateOpen(false);
  }, []);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      // Don't intercept when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'Escape')     onBack?.();
      if (e.key === 'b' || e.key === 'B') {
        if (issue) {
          setBookmarks(bms => toggleBookmark(issue.id, currentPage, bms));
        }
      }
      if (e.key === 't' || e.key === 'T') setShowTOC(o => !o);
      if (e.key === 'z' || e.key === 'Z') resetZoom();
      if (e.key === 'm' || e.key === 'M') setReaderMode(m => m === 'dark' ? 'light' : 'dark');
      if (e.key === '/') { e.preventDefault(); setShowSearch(o => !o); }
      if (e.key === 'a' || e.key === 'A') setShowTextMode(o => !o);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onBack, issue, currentPage, resetZoom]);

  // ── Wheel zoom ───────────────────────────────────────────────────────────────
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.max(0.75, Math.min(3.5, z + e.deltaY * -0.001)));
  }, []);

  // ── Mouse drag-to-pan ────────────────────────────────────────────────────────
  const onMouseDown = useCallback((e) => {
    if (zoom <= 1) return;
    dragging.current    = true;
    dragStartX.current  = e.clientX;
    dragStartY.current  = e.clientY;
    dragPanStartX.current = panX;
    dragPanStartY.current = panY;
    e.preventDefault();
  }, [zoom, panX, panY]);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStartX.current;
    const dy = e.clientY - dragStartY.current;
    setPanX(dragPanStartX.current + dx);
    setPanY(dragPanStartY.current + dy);
  }, []);

  const onMouseUp = useCallback(() => { dragging.current = false; }, []);

  // ── Double-click zoom toggle ─────────────────────────────────────────────────
  const onDoubleClick = useCallback(() => {
    if (zoom !== 1) {
      resetZoom();
    } else {
      setZoom(2);
    }
  }, [zoom, resetZoom]);

  // ── Touch handlers ───────────────────────────────────────────────────────────
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      pinchActive.current  = true;
      initialDist.current  = getTouchDist(e.touches[0], e.touches[1]);
      initialZoom.current  = zoom;
    } else if (e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  }, [zoom]);

  const onTouchMove = useCallback((e) => {
    if (pinchActive.current && e.touches.length === 2) {
      e.preventDefault();
      const newDist = getTouchDist(e.touches[0], e.touches[1]);
      const newZoom = Math.max(0.75, Math.min(3.5, initialZoom.current * (newDist / initialDist.current)));
      setZoom(newZoom);
    } else if (e.touches.length === 1 && zoom > 1) {
      // pan
      const dx = e.touches[0].clientX - (touchStartX.current || e.touches[0].clientX);
      const dy = e.touches[0].clientY - (touchStartY.current || e.touches[0].clientY);
      setPanX(p => p + dx);
      setPanY(p => p + dy);
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  }, [zoom]);

  const onTouchEnd = useCallback(() => {
    if (pinchActive.current) {
      pinchActive.current = false;
      return;
    }
    // At zoom === 1, react-pageflip handles swipe-to-flip natively via its own
    // touch listeners on the book element. We just clear our pan-tracking refs.
    // At zoom > 1, pan already completed during touchmove; nothing else to do.
    touchStartX.current = null;
    touchStartY.current = null;
  }, []);

  // ── Share ─────────────────────────────────────────────────────────────────────
  const handleShare = useCallback(() => {
    setShowSharePopover(prev => !prev);
  }, []);

  // ── Bookmark current page ────────────────────────────────────────────────────
  const handleToggleBookmarkCurrent = useCallback(() => {
    if (!issue) return;
    setBookmarks(bms => toggleBookmark(issue.id, currentPage, bms));
  }, [issue, currentPage]);

  const handleToggleBookmarkPage = useCallback((pageNum) => {
    if (!issue) return;
    setBookmarks(bms => toggleBookmark(issue.id, pageNum, bms));
  }, [issue]);

  // ── Intro dismiss ────────────────────────────────────────────────────────────
  const handleIntroDismiss = useCallback(() => {
    if (issue) sessionStorage.setItem(`lwd_intro_${issue.id}`, '1');
    setShowIntro(false);
  }, [issue]);

  // ── PDF download ──────────────────────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    if (!issue?.pdf_url) return;
    trackDownload(issue.id);
    window.open(issue.pdf_url, '_blank', 'noreferrer');
  }, [issue]);

  // ── Hotspot click handler ────────────────────────────────────────────────────
  const handleHotspotClick = useCallback((hs) => {
    // Track the click
    if (issue?.id) {
      supabase.from('magazine_read_events').insert({
        issue_id: issue.id,
        session_id: sessionId.current,
        event_type: 'hotspot_click',
        page_number: currentPage,
        referrer: hs.label || hs.url || null,
      }).catch(() => {});
    }

    if (hs.type === 'vendor' && hs.url) {
      setActiveHotspot(hs);
    } else if (hs.url) {
      window.open(hs.url, '_blank', 'noreferrer');
    }
  }, [issue?.id, currentPage]);

  // ── Paywall (moved earlier, see "before Helpers" above) ─────────────────────
  // paywallBlocking is referenced inside goNext's useCallback, so must be declared
  // before that callback to avoid a TDZ ReferenceError.
  const paywallEnabled  = issue?.paywall_enabled === true;
  const freePageCount   = issue?.free_page_count ?? 3;

  // ── Page size → aspect ratio ─────────────────────────────────────────────────
  // Used to set correct proportions when no image is loaded yet
  const PAGE_RATIOS = { A4: 1.414, A5: 1.414, US_LETTER: 1.294, SQUARE: 1.0, TABLOID: 1.545 };
  const pageAspect = PAGE_RATIOS[issue?.page_size || 'A4'] || 1.414;

  // ── Spread mode (declared earlier, see "before Helpers" above) ─────────────
  const forceSpread = forceSpreadEarly; // alias kept for readability below

  // ── Derive spread context for credits / aspect ratio ─────────────────────────
  // react-pageflip owns the render loop internally; these derivations just feed
  // credit metadata and aspect-ratio sizing of the flipbook container.
  const pageByNum      = (n) => pages.find(p => p.page_number === n) || null;
  const leftPage       = useDoubleSpread && currentPage > 1 ? pageByNum(currentPage)     : null;
  const rightPage      = useDoubleSpread
    ? (currentPage === 1 ? pageByNum(1) : pageByNum(currentPage + 1))
    : pageByNum(currentPage);
  const isDoubleSpread = useDoubleSpread && currentPage > 1;

  // ── Spread credits ───────────────────────────────────────────────────────────
  const spreadCredits = useMemo(() => {
    const creditPages = [leftPage, rightPage].filter(Boolean);
    return creditPages.flatMap(p => p?.vendor_credits || []);
  }, [leftPage, rightPage]);
  const spreadHasCredits = spreadCredits.length > 0;
  const spreadCreditCount = spreadCredits.length;

  // Prerender: ready once the issue has resolved (title, SEO, JSON-LD set below).
  // Must run before the early returns, hooks can't follow a conditional return.
  usePrerenderReady(!loading && !!issue);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) return <Spinner />;
  if (error)   return <ErrorScreen message={error} onBack={onBack} />;
  if (!issue)  return <ErrorScreen message="Issue not found." onBack={onBack} />;
  if (pages.length === 0) {
    return <ErrorScreen message={isPreview ? "No pages published yet, publish the issue first, then preview." : "This issue has no pages yet. Check back soon."} onBack={onBack} />;
  }

  // Portrait mobile → prompt to rotate. The reader is designed for landscape
  // (or desktop spread). Portrait on a phone gives a cramped single-page view
  // and has caused black-screen rendering issues. Rotate prompt is cleaner.
  if (!viewportWide) {
    return <RotatePrompt onBack={onBack} T={T} />;
  }

  const isBookmarkedCurrent = bookmarks.includes(currentPage);

  // ── SEO meta tags ─────────────────────────────────────────────────────────────
  const canonicalUrl  = `https://luxuryweddingdirectory.com/publications/${issue.slug}`;
  const ogImage       = imgUrl(issue.og_image_url || issue.cover_image, { width: 1200, resize: 'contain' }) || '';
  const metaTitle     = issue.seo_title    || issue.title       || 'LWD Magazine';
  const metaDesc      = issue.seo_description || issue.intro
    || `Read ${issue.title}, the luxury wedding editorial magazine from Luxury Wedding Directory.`;
  const pageTitle     = `${metaTitle} | Luxury Wedding Directory Magazine`;

  return (
    <>
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type"        content="article" />
      <meta property="og:title"       content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url"         content={canonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:width"  content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      <meta property="og:site_name"   content="Luxury Wedding Directory" />

      {/* Twitter card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* RSS autodiscovery */}
      <link rel="alternate" type="application/rss+xml"
            title="LWD, The Magazine"
            href="https://luxuryweddingdirectory.com/magazine-feed.xml" />
    </Helmet>
    <div
      style={{
        position: 'fixed', inset: 0,
        background: T.bg,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', userSelect: 'none',
        transition: 'background 0.4s ease',
      }}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* ── Draft preview banner ──────────────────────────────────────────── */}
      {isPreview && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 9999,
          background: 'rgba(201,169,110,0.95)',
          color: '#1a1714',
          fontFamily: "'Jost', sans-serif",
          fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase',
          padding: '6px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8,
        }}>
          <span>◆ Draft Preview, not visible to the public</span>
          <span style={{ opacity: 0.6, fontWeight: 400, letterSpacing: '0.04em', textTransform: 'none', fontSize: 10 }}>
            Publish to make live
          </span>
        </div>
      )}

      {/* ① Reading progress bar */}
      <ProgressBar currentPage={currentPage} totalPages={totalPages} />

      {/* Top bar */}
      <ReaderTopBar
        issue={issue}
        onBack={onBack}
        textEditionSlug={embedded ? null : textEditionSlug}
        onDownload={issue.pdf_url ? handleDownload : null}
        showTOC={showTOC}
        onToggleTOC={() => {
          // Intent trigger: opening TOC before unlock = "I want to find something" = high intent
          if (!showTOC && !gateUnlocked && !gateTriggeredRef.current) {
            triggerGateByIntent('intent_nav');
            return;
          }
          setShowTOC(o => !o);
        }}
        bookmarked={isBookmarkedCurrent}
        onToggleBookmark={handleToggleBookmarkCurrent}
        onShare={handleShare}
        onExport={() => setShowExportModal(true)}
        onSearch={() => setShowSearch(o => !o)}
        onTextMode={() => setShowTextMode(o => !o)}
        readerMode={readerMode}
        onToggleMode={() => setReaderMode(m => m === 'dark' ? 'light' : 'dark')}
        T={T}
      />

      {/* Fullscreen button, landscape only, shown in bottom-right corner */}
      {document.fullscreenEnabled && (
        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          style={{
            position: 'fixed', bottom: 20, right: 20, zIndex: 120,
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, transition: 'border-color 0.18s, color 0.18s',
            WebkitTapHighlightColor: 'transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        >
          {isFullscreen ? '⊠' : '⛶'}
        </button>
      )}

      {/* ③ TOC Panel */}
      <ReaderTOCPanel
        open={showTOC}
        onClose={() => setShowTOC(false)}
        pages={pages}
        currentPage={currentPage}
        onJump={goToPage}
        bookmarks={bookmarks}
        onToggleBookmark={handleToggleBookmarkPage}
        T={T}
      />

      {/* ⑥ In-reader Search Panel */}
      {showSearch && (
        <IssueSearchPanel
          pages={pages}
          currentPage={currentPage}
          onJump={(n) => { goToPage(n); setShowSearch(false); }}
          onClose={() => setShowSearch(false)}
          T={T}
        />
      )}

      {/* ⑥ Text / Accessibility Mode */}
      {showTextMode && (
        <TextModePanel
          pages={pages}
          issue={issue}
          onClose={() => setShowTextMode(false)}
          T={T}
        />
      )}

      {/* Main viewer, padding is minimal so the page fits the screen on load.
          Desktop: 16px top/bottom + 72px sides (room for nav-button circles).
          Mobile:  8px top/bottom, no side padding, full-bleed page.         */}
      <div
        style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isDesktop ? '16px 72px' : '8px 0',
          position: 'relative', overflow: 'hidden',
          cursor: zoom > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'default',
          // Perspective creates the 3D field for the paper-curl flip on
          // desktop. On mobile WebKit (iOS Safari especially) perspective
          // on a flex parent with transformed children is a known cause
          // of black-screen / compositing bugs. Disable on mobile, flip
          // still works, just without the 3D depth.
          perspective: isDesktop ? '1200px' : 'none',
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* ② LuxuryFlipBook, direct StPageFlip engine wrapper.
            Replaces the abandoned react-pageflip shim. The wrapper
            resolved the mobile black-screen issue plus restored proper
            destroy/reinitialise on viewport-breakpoint changes via the
            key prop pattern.

            Hotspots, video overlays, paywall gates, and the email gate
            stay layered above this component (managed by the parent),
            so all editorial features remain functional. Only the page-
            flip engine + page-image rendering have moved into here. */}
        {/* Cover-centring wrapper. In spread mode, page-flip places the
            cover on the right half of the spread (book-on-shelf). On
            page 1 we slide the whole flipbook left by 25% of its width
            so the cover reads as centred. As soon as the user flips to
            page 2+, this returns to 0, combined with the engine's own
            flip animation it feels like the book is opening: cover
            slides right, page 2 reveals left. */}
        <div style={{
          display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          width: '100%', height: '100%',
          // Cover sits alone on the RIGHT half → slide left to centre it.
          // Back cover (last page) sits alone on the LEFT half → slide right
          // to centre it (mirror of the cover case). Without this the back
          // cover reads off-centre on the left.
          transform: (useDoubleSpread && viewportWide && currentPage === 1)
            ? 'translateX(-25%)'
            : (useDoubleSpread && viewportWide && currentPage === pages.length)
            ? 'translateX(25%)'
            : 'translateX(0)',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          ...(viewportWide
            ? {
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                aspectRatio: useDoubleSpread
                  ? `${2 / pageAspect} / 1`
                  : `${1 / pageAspect} / 1`,
                transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
                transformOrigin: 'center center',
                transition: dragging.current ? 'none' : undefined,
              }
            : {
                width:  '94vw',
                height: `calc(94vw * ${pageAspect})`,
                maxHeight: 'calc(100% - 16px)',
              }
          ),
          // Background is ALWAYS transparent. The page images are opaque, so
          // interior two-page spreads look identical, but a lone cover /
          // back-cover (StPageFlip fills only one half of the canvas) can never
          // show a white page rectangle on the empty half, even if the engine's
          // reported page is briefly stale after a reverse flip. The empty half
          // simply shows the reader backdrop. The drop shadow is still dropped
          // on cover/back views so the lone page doesn't carry a full-spread
          // shadow halo.
          boxShadow: (useDoubleSpread && viewportWide && (currentPage === 1 || currentPage === pages.length))
            ? 'none' : T.pageShad,
          background: 'transparent',
        }}>
          <LuxuryFlipBook
            // Key change forces a clean destroy + reconstruct of the
            // engine when the viewport breakpoint crosses 640px. Each
            // mode (portrait / spread) gets a fresh PageFlip instance
            // with the correct config, no stale-state black screens.
            key={viewportWide ? 'flipbook-spread' : 'flipbook-portrait'}
            ref={flipBookRef}
            pages={pages}
            startPage={currentPage}
            isPortrait={!useDoubleSpread}
            pageBg={T.pageBg}
            pageAspect={pageAspect}
            onFlip={(newPage) => {
              if (newPage !== currentPage) {
                firePageTurn(newPage);
                setCurrentPage(newPage);
              }
              syncingRef.current = false;
            }}
            onPageView={(pageNumber) => {
              // Analytics-only, separate from URL sync (handled in onFlip).
              // Currently a no-op; trackPageTurn fires inside firePageTurn.
              // This hook lets us split first-page vs subsequent flips later
              // if needed.
            }}
            onReady={() => {
              // Fires after the engine's 'init' event. Useful spot for
              // any post-mount work (focus management, analytics ping).
            }}
            // Dev-only diagnostic overlay. Strips itself in production
            // builds via import.meta.env.DEV check inside the component.
            debug={false}
          />
        </div>
        </div>

        {/* ── Flipbook overlays: "Watch Film" + back-cover "Contact the Venue" ──
            The flipbook renders page images only, so per-page video + the contact
            CTA are layered here, keyed to the current page. Contact writes a tracked
            lead (inquiries, source='magazine_back_cover'). */}
        {pages.length > 0 && (() => {
          const onBackCover = currentPage === pages.length && pages.length > 1;
          const curPage     = pages[currentPage - 1];
          const curVideo    = curPage?.video_url;
          const curAutoplay = curPage?.video_autoplay;
          return (
            <>
              {curVideo && !curAutoplay && !filmOpen && (
                <button
                  onClick={() => setFilmOpen(true)}
                  style={{ position: 'absolute', bottom: 92, right: 24, zIndex: 120,
                    background: 'rgba(0,0,0,0.72)', border: `1px solid ${GOLD}`, color: GOLD,
                    padding: '9px 18px', borderRadius: 2, fontFamily: NU, fontSize: 10, fontWeight: 600,
                    letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                >▶ Watch Film</button>
              )}
              {onBackCover && (
                <BackCoverContact pageNumber={pages.length} onClick={openBackCoverContact}
                  box={(pages[pages.length - 1]?.link_targets || []).find(t => t?.type === 'cta')} />
              )}
            </>
          );
        })()}
        {/* Embedded inline film page (autoplay page, e.g. page 7) */}
        {(() => {
          const embedFilm = pages.find(p => p.video_url && p.video_autoplay);
          if (!embedFilm) return null;
          return (
            <EmbeddedPageVideo
              pageNumber={embedFilm.page_number}
              url={embedFilm.video_url}
              active={useDoubleSpread
                ? (currentPage === embedFilm.page_number || currentPage === embedFilm.page_number - 1)
                : currentPage === embedFilm.page_number}
            />
          );
        })()}
        {filmOpen && (
          <FilmModal url={pages[currentPage - 1]?.video_url} onClose={() => setFilmOpen(false)} />
        )}
        {enquiryOpen && venueForEnquiry && (
          <LuxeEnquiryModal
            venue={venueForEnquiry}
            entityType="venue"
            leadSourceOverride="magazine_back_cover"
            onClose={() => setEnquiryOpen(false)}
          />
        )}

        {/* Nav buttons */}
        <NavButton direction="prev" onClick={goPrev} disabled={!canPrev} T={T} />
        <NavButton direction="next" onClick={goNext} disabled={!canNext || paywallBlocking} T={T} />

        {/* Paywall gate, renders over pages when limit reached */}
        {paywallBlocking && (
          <PaywallGate
            issue={issue}
            freePageCount={freePageCount}
            onBack={onBack}
            T={T}
          />
        )}

        {/* Email gate, lead capture (Phase 3). Renders over pages, paywall takes priority. */}
        {gateOpen && !gateUnlocked && !paywallBlocking && (
          <EmailGate
            issue={issue}
            slug={slug}
            currentPage={currentPage}
            triggerReason={gateReason}
            onUnlock={handleGateUnlock}
            onSkip={handleGateSkip}
          />
        )}
      </div>

      {/* ② Zoom reset pill */}
      <ZoomPill zoom={zoom} onReset={resetZoom} T={T} />

      {/* Enhanced page counter */}
      <PageCounter
        currentPage={currentPage}
        totalPages={totalPages}
        isDouble={isDoubleSpread}
        bookmarks={bookmarks}
        T={T}
      />

      {/* Page reactions */}
      {!gateOpen && (
        <PageReactions
          currentPage={currentPage}
          counts={reactionCounts}
          myReactions={myReactions}
          onReact={handleReaction}
          T={T}
        />
      )}

      {/* Thumbnail strip (desktop, secondary) */}
      <ThumbnailStrip
        pages={pages}
        currentPage={currentPage}
        onJump={(n) => {
          // Intent trigger: jumping via thumbnails before unlock = high intent
          if (!gateUnlocked && !gateTriggeredRef.current) {
            triggerGateByIntent('intent_nav');
            return;
          }
          goToPage(n);
        }}
        isDesktop={isDesktop}
        T={T}
      />

      {/* Vendor Credits button */}
      {spreadHasCredits && (
        <VendorCreditsButton
          count={spreadCreditCount}
          open={creditsOpen}
          onToggle={() => setCreditsOpen(o => !o)}
          T={T}
        />
      )}

      {/* Credits drawer */}
      {creditsOpen && (
        <CreditsDrawer
          credits={spreadCredits}
          onClose={() => setCreditsOpen(false)}
          T={T}
        />
      )}

      {/* In-reader enquiry modal */}
      {activeHotspot && (
        <InReaderEnquiryModal
          hotspot={activeHotspot}
          onClose={() => setActiveHotspot(null)}
        />
      )}

      {/* Social export modal */}
      {showExportModal && (
        <SocialExportModal
          page={rightPage || pages[0]}
          issue={issue}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* ⑤ Share toast */}
      <Toast message="Link copied!" visible={toastVisible} />

      {/* ⑤b Share popover */}
      {showSharePopover && (() => {
        const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
        const shareTitle = issue?.title || 'Luxury Wedding Directory';
        const twUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        const waUrl = `https://wa.me/?text=${encodeURIComponent(`${shareTitle}, ${shareUrl}`)}`;
        const piUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(shareTitle)}`;
        return (
          <>
            {/* Backdrop, click outside to close */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 1199 }}
              onClick={() => setShowSharePopover(false)}
            />
            <div style={{
              position: 'fixed', top: 64, right: 20, zIndex: 1200,
              background: 'rgba(14,12,9,0.97)',
              border: '1px solid rgba(211,154,33,0.25)',
              borderRadius: 10,
              padding: '18px 20px',
              minWidth: 220,
              boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ fontFamily: NU, fontSize: 9, fontWeight: 700, color: GOLD, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4 }}>
                Share this issue
              </div>
              {/* Copy link */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    setShowSharePopover(false);
                    setToastVisible(true);
                    setTimeout(() => setToastVisible(false), 2200);
                  });
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '9px 14px', cursor: 'pointer', fontFamily: NU, fontSize: 12, color: '#fff', textAlign: 'left' }}
              >
                <span style={{ fontSize: 14 }}>⎘</span> Copy link
              </button>
              {/* Twitter / X */}
              <a
                href={twUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => setShowSharePopover(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '9px 14px', textDecoration: 'none', fontFamily: NU, fontSize: 12, color: '#fff' }}
              >
                <span style={{ fontSize: 14, color: GOLD }}>𝕏</span> Share on X
              </a>
              {/* WhatsApp */}
              <a
                href={waUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => setShowSharePopover(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '9px 14px', textDecoration: 'none', fontFamily: NU, fontSize: 12, color: '#fff' }}
              >
                <span style={{ fontSize: 14, color: GOLD }}>💬</span> WhatsApp
              </a>
              {/* Pinterest */}
              <a
                href={piUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => setShowSharePopover(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '9px 14px', textDecoration: 'none', fontFamily: NU, fontSize: 12, color: '#fff' }}
              >
                <span style={{ fontSize: 14, color: GOLD }}>📌</span> Pinterest
              </a>
            </div>
          </>
        );
      })()}

      {/* ⑧ Cinematic intro */}
      {showIntro && (
        <IntroOverlay issue={issue} onDismiss={handleIntroDismiss} />
      )}

      {/* ⑨ Personalised cover overlay */}
      {showPersonalisedCover && personalisedData && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 600,
            background: '#080706',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 0,
            animation: 'introReveal 0.8s ease forwards',
          }}
          onClick={() => setShowPersonalisedCover(false)}
        >
          {/* Blurred backdrop */}
          {issue?.cover_image && (
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `url(${imgUrl(issue.cover_image, { width: 400, resize: 'contain' })})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
              filter: 'blur(40px) brightness(0.2)',
            }} />
          )}
          <div style={{
            position: 'relative', display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 24, padding: '40px 32px',
            maxWidth: 480, textAlign: 'center',
          }}>
            {/* Gold top border */}
            <div style={{ width: 64, height: 2, background: '#d39a21', marginBottom: 8 }} />
            {/* "For" label */}
            <div style={{ fontFamily: NU, fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(211,154,33,0.7)' }}>
              A Personal Edition for
            </div>
            {/* Couple names */}
            <div style={{ fontFamily: GD, fontSize: 40, fontStyle: 'italic', fontWeight: 400, color: '#d39a21', lineHeight: 1.2, margin: '4px 0' }}>
              {[personalisedData.partner1_name, personalisedData.partner2_name].filter(Boolean).join(' & ')}
            </div>
            {/* Wedding date */}
            {personalisedData.wedding_date && (
              <div style={{ fontFamily: NU, fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.12em' }}>
                {new Date(personalisedData.wedding_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
            {/* Venue name */}
            {personalisedData.venue_name && (
              <div style={{ fontFamily: GD, fontSize: 16, fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', marginTop: -12 }}>
                {personalisedData.venue_name}
              </div>
            )}
            {/* Issue name */}
            <div style={{ fontFamily: NU, fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 8 }}>
              {issue?.title || 'Luxury Wedding Directory'}
            </div>
            <div style={{ width: 64, height: 1, background: 'rgba(211,154,33,0.3)', marginTop: 4 }} />
            {/* Dismiss hint */}
            <div style={{ fontFamily: NU, fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 8 }}>
              Tap anywhere to read
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
